export type LocationMode = "none" | "address";

/** UI delivery options — `courier` expands to courier + express-courier. */
export type DeliveryMethodUi = "none" | "shipping" | "courier" | "pickup";

export type GetVendorsFilteredDebugFormState = {
	page: string;
	pageSize: string;
	deliveryMethodUi: DeliveryMethodUi;
	expressCourierOnly: boolean;
	combinedPaymentEnabled: boolean;
	isTop: "any" | "true" | "false";
	maxDistance: string;
	locationMode: LocationMode;
	/** FulfillmentService DeliveryAddress fields */
	postalCode: string;
	streetName: string;
	houseNr: string;
	city: string;
	streetAdditional: string;
	sortBy: "name" | "freeFrom" | "distance";
	sortOrder: "asc" | "desc";
};

export const GET_VENDORS_FILTERED_DEBUG_INITIAL_FORM: GetVendorsFilteredDebugFormState =
	{
		page: "0",
		pageSize: "10",
		deliveryMethodUi: "none",
		expressCourierOnly: false,
		combinedPaymentEnabled: false,
		isTop: "any",
		maxDistance: "",
		locationMode: "none",
		postalCode: "10117",
		streetName: "Unter den Linden",
		houseNr: "1",
		city: "Berlin",
		streetAdditional: "",
		sortBy: "name",
		sortOrder: "asc",
	};

export const debugFieldClass =
	"w-full rounded border border-gray-300 bg-white px-2 py-1 text-body-sm";
export const debugLabelClass = "flex flex-col gap-1 text-body-sm text-gray-700";

/**
 * Maps UI delivery selection to API `deliveryMethodKinds`.
 * - shipping → ["shipping"]
 * - courier → ["courier"] (query adds express open within 10 km)
 * - pickup → ["pickup"]
 * - expressCourierOnly → ["express-courier"] (overrides)
 */
export function resolveDeliveryMethodKinds(
	deliveryMethodUi: DeliveryMethodUi,
	expressCourierOnly: boolean,
): string[] | undefined {
	if (expressCourierOnly) {
		return ["express-courier"];
	}
	switch (deliveryMethodUi) {
		case "shipping":
			return ["shipping"];
		case "courier":
			return ["courier"];
		case "pickup":
			return ["pickup"];
		default:
			return undefined;
	}
}

/**
 * Builds a `GetVendorsFilteredInput`-shaped payload from the debug form.
 */
export function buildGetVendorsFilteredDebugInput(
	form: GetVendorsFilteredDebugFormState,
): Record<string, unknown> {
	const input: Record<string, unknown> = {
		page: Number(form.page) || 0,
		pageSize: Number(form.pageSize) || 10,
		sort: { sortBy: form.sortBy, sortOrder: form.sortOrder },
	};
	const filter: Record<string, unknown> = {};
	const deliveryMethodKinds = resolveDeliveryMethodKinds(
		form.deliveryMethodUi,
		form.expressCourierOnly,
	);
	if (deliveryMethodKinds) {
		filter.deliveryMethodKinds = deliveryMethodKinds;
	}
	if (form.combinedPaymentEnabled) {
		filter.combinedPaymentEnabled = true;
	}
	if (form.isTop === "true") {
		filter.isTop = true;
	} else if (form.isTop === "false") {
		filter.isTop = false;
	}
	if (form.maxDistance.trim() !== "") {
		filter.maxDistance = Number(form.maxDistance);
	}
	if (Object.keys(filter).length > 0) {
		input.filter = filter;
	}
	if (form.locationMode === "address") {
		input.location = {
			postalCode: form.postalCode,
			...(form.streetName.trim() ? { streetName: form.streetName.trim() } : {}),
			...(form.houseNr.trim() ? { houseNr: form.houseNr.trim() } : {}),
			...(form.city.trim() ? { city: form.city.trim() } : {}),
			...(form.streetAdditional.trim()
				? { streetAdditional: form.streetAdditional.trim() }
				: {}),
		};
	}
	return input;
}
